import { useCallback, useRef } from "react";

// useScrollBottom — pins a scroll container to its bottom on open / switch /
// send. Returns a `ref` to attach to the scrollable element, plus an imperative
// `scrollToBottom()` (synchronous) and `scrollToBottomAfterPaint()` (deferred
// until the browser has measured the new content height).
//
// WHY scrollTop = scrollHeight (not scrollIntoView on a sentinel): the hub has
// NESTED overflow-y-auto containers — HubShell's <main>, the reading-pane
// <main>, and this list. scrollIntoView on a child races whichever ancestor
// the browser picks and fires before layout settles, so it visibly does
// nothing. Setting scrollTop directly on the real scroll container is
// unambiguous and measured after layout.
//
// Usage in a stream:
//   const { ref, scrollToBottomAfterPaint } = useScrollBottom();
//   useEffect(() => { if (status === "ready") scrollToBottomAfterPaint(); },
//     [status, id]);                       // open / switch
//   const onSent = async () => { await refresh(); scrollToBottomAfterPaint(); }; // send
//   <div ref={ref} className="... overflow-y-auto ...">
//
// Do NOT scroll after "Load more" — older messages prepend at the top and
// pinning to the bottom would yank the reader away from what they're reading.
//
// Timing: a single rAF can run before the browser measures the new content
// height, so scrollToBottomAfterPaint schedules TWO rAFs (paint → measure).
export function useScrollBottom() {
  const ref = useRef(null);

  const scrollToBottom = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const scrollToBottomAfterPaint = useCallback(() => {
    requestAnimationFrame(() => {
      scrollToBottom();
      // second rAF: re-apply after the browser has laid out the new messages
      // (handles the case where the first frame's scrollHeight was stale).
      requestAnimationFrame(scrollToBottom);
    });
  }, [scrollToBottom]);

  return { ref, scrollToBottom, scrollToBottomAfterPaint };
}

export default useScrollBottom;

